import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Group, Stack, Textarea } from '@mantine/core';

import MermaidRenderer from './MermaidRenderer';

interface SimpleMermaidEditorProps {
    value: string;
    onChange: (value: string) => void;
    height?: string | number;
    editable?: boolean;
    onAiGenerate?: (mermaidCode: string) => void;
    onEdit?: () => void;
}

/**
 * Simplified MermaidEditor that works with value/onChange pattern.
 * No wrapper Paper or title - just the editor functionality.
 */
const SimpleMermaidEditor: React.FC<SimpleMermaidEditorProps> = ({
    value,
    onChange,
    height = 400,
    editable = true,
    onAiGenerate,
    onEdit
}) => {
    const [mermaidCode, setMermaidCode] = useState(value);
    const [isEditing, setIsEditing] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (!isEditing) {
            setMermaidCode(value);
            setIsDirty(false);
        }
    }, [value, isEditing]);

    const handleCodeChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newValue = event.target.value;
            setMermaidCode(newValue);
            setIsDirty(newValue !== value);
        },
        [value]
    );

    const handleSave = useCallback(() => {
        onChange(mermaidCode);
        setIsDirty(false);
        setIsEditing(false);
    }, [mermaidCode, onChange]);

    const handleCancel = useCallback(() => {
        setMermaidCode(value);
        setIsDirty(false);
        setIsEditing(false);
    }, [value]);

    const toggleEdit = useCallback(() => {
        setIsEditing(!isEditing);
    }, [isEditing]);

    return (
        <Stack gap='sm' style={{ position: 'relative' }}>
            {editable && isEditing && (
                <Group
                    gap='xs'
                    style={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        zIndex: 10
                    }}
                >
                    <Button
                        variant='filled'
                        size='xs'
                        color='blue'
                        onClick={handleSave}
                        disabled={!isDirty}
                    >
                        Save
                    </Button>
                    <Button
                        variant='subtle'
                        size='xs'
                        color='gray'
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </Group>
            )}
            {isEditing ? (
                <Textarea
                    ref={textareaRef}
                    value={mermaidCode}
                    onChange={handleCodeChange}
                    placeholder='Enter Mermaid diagram code...'
                    minRows={10}
                    maxRows={20}
                    autosize
                    style={{ fontFamily: 'monospace' }}
                    styles={{
                        input: {
                            fontSize: '14px',
                            lineHeight: 1.4
                        }
                    }}
                />
            ) : (
                <MermaidRenderer
                    chart={mermaidCode}
                    height={height}
                    onAiGenerate={onAiGenerate}
                    onEdit={onEdit ?? (editable ? toggleEdit : undefined)}
                />
            )}
        </Stack>
    );
};

export default SimpleMermaidEditor;
